export function allocateHelperLots(data, name, kg, trace = "") {
  let remaining = Math.max(Number(kg) || 0, 0);
  const lots = stockLedger(data, "Hulpstof").lots.filter((l) => l.names.includes(name) && l.tracecode && l.rest > 0 && (!trace || l.tracecode === trace)).sort((a, b) => a.datum.localeCompare(b.datum) || a.key.localeCompare(b.key));
  const result = [];
  lots.forEach((l) => { const take = Math.min(remaining, l.rest); if (take > 0) result.push({ tracecode: l.tracecode, kg: take }); remaining -= take; });
  if (remaining > 0) result.push({ tracecode: trace || "", kg: remaining });
  return result;
}


const n = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const text = (value) => String(value ?? "").trim();
const clean = (value) => Math.abs(value) < 1e-8 ? 0 : Number(value.toFixed(6));
export const stockTrace = (row) => text(row?.eigenTracecode || row?.tracecode || row?.batchnr);
export const stockKey = (material, trace) => JSON.stringify([material, trace]);
const sourceKeys = ["expeditie", "hulpstofInkoop", "entries", "halffabRuns", "afkeur"];

export function stockCatalog(data, category) {
  const masters = category === "Grondstof" ? data.rawMaterials || [] : data.helpers || [];
  const groups = new Map();
  masters.filter((m) => m?.naam).forEach((m) => {
    const article = text(m.artikelnummer || m.artikelnr);
    const key = article ? `art:${article.toLowerCase()}` : `naam:${m.naam}`;
    const g = groups.get(key) || { material: key, naam: m.naam, artikelnummer: article, type: m.type || "", prijs: n(m.prijs), leverancier: m.leverancier || "", names: [], correctie: 0 };
    if (!g.names.includes(m.naam)) g.names.push(m.naam);
    g.correctie += n(m.correctie);
    groups.set(key, g);
  });
  return [...groups.values()];
}

export function stockLedger(data = {}, category = "Grondstof", peildatum = "") {
  const catalog = stockCatalog(data, category);
  const byName = new Map(catalog.flatMap((m) => m.names.map((name) => [name, m])));
  const record = (data.voorraadTellingen || []).find((r) => r.id === category);
  // Effectieve teldatum: als er een handmatige doelmaand (JJJJ-MM) is gekozen, telt de telling mee
  // tot het einde van die maand; anders de werkelijke registratietijd. Puur additief — zonder
  // peildatum (alle huidige schermen) verandert er niets.
  const sessDatum = (s) => {
    const dm = text(s.doelmaand);
    if (/^\d{4}-\d{2}$/.test(dm)) { const [y, m] = dm.split("-").map(Number); return `${dm}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`; }
    return text(s.ts).slice(0, 10);
  };
  const sessions = (record?.sessions || []).filter((s) => !peildatum || sessDatum(s) <= peildatum);
  const countedMaterials = new Map();
  sessions.forEach((s) => s.lines.forEach((l) => {
    countedMaterials.set(l.naam, { material: l.material, naam: l.naam, names: [l.naam], artikelnummer: l.artikelnummer || "", prijs: n(l.prijs), leverancier: l.leverancier || "", type: "" });
  }));
  const materialFor = (name, details = {}) => {
    const master = byName.get(name);
    const counted = countedMaterials.get(name);
    return { ...(master || { material: `naam:${name}`, naam: name, names: [name], artikelnummer: "", type: details.type || "", prijs: n(details.prijs), leverancier: details.leverancier || "" }), ...(counted ? { material: counted.material } : {}), needsMaster: !master };
  };
  const receipts = category === "Grondstof" ? data.expeditie || [] : data.hulpstofInkoop || [];
  const receiptSource = category === "Grondstof" ? "expeditie" : "hulpstofInkoop";
  const lots = new Map(), receiptLots = new Map(), events = [], allocations = [];
  const inPeriod = (date) => !peildatum || text(date).slice(0, 10) <= peildatum;
  const ensureLot = (material, trace, details = {}) => {
    const key = stockKey(material.material, trace);
    if (!lots.has(key)) lots.set(key, { ...material, key, tracecode: trace, datum: details.datum || "", leverancier: details.leverancier || material.leverancier, prijs: details.prijs == null ? material.prijs : n(details.prijs), rest: 0, ontvangen: 0, verbruikt: 0, ids: [], counted: false, newlyReceived: false });
    return lots.get(key);
  };
  const unresolved = (material) => ensureLot(material, "", { leverancier: "Niet aan een tracecode gekoppeld" });
  receipts.filter((r) => r && inPeriod(r.datum) && (category !== "Grondstof" || !r.type || r.type === category)).forEach((r) => {
    const name = r.grondstof || r.hulpstof;
    const m = materialFor(name, r);
    const lot = ensureLot(m, stockTrace(r), r);
    lot.ids.push(text(r.id));
    receiptLots.set(text(r.id), lot.key);
    events.push({ source: receiptSource, id: text(r.id), eventKey: `${receiptSource}:${r.id}`, datum: r.datum || "", material: m.material, key: lot.key, kg: n(r.kg), receipt: true });
  });
  const addUse = (source, row, name, kg, trace, receiptId, suffix = "", fixed = false) => {
    if (!name || !inPeriod(row.datum) || !(n(kg) > 0)) return;
    const m = materialFor(name);
    const linked = receiptLots.get(text(receiptId));
    const key = linked || (trace ? ensureLot(m, text(trace), row).key : fixed ? unresolved(m).key : "");
    unresolved(m);
    events.push({ source, id: text(row.id), eventKey: `${source}:${row.id}:${suffix}`, datum: row.datum || "", material: m.material, key, kg: n(kg), receipt: false });
  };
  const halfNames = new Set((data.halffabRuns || []).map((r) => r.recept));
  (data.entries || []).forEach((e) => {
    if (category === "Grondstof" && !halfNames.has(e.grondstof)) addUse("entries", e, e.grondstof, Math.max(n(e.grondstofKg) - n(e.restantKg), 0), e.grondstofTrace, e.expeditieId);
    else if (category === "Hulpstof") ["predust", "battermix", "breader", "hamkaas"].forEach((f) => {
      if (f === "hamkaas" && Array.isArray(e.hamkaasLots) && e.hamkaasLots.length) return; // gesneden ham/kaas is al bij het snijden afgeboekt (invriespartij); niet dubbel boeken
      const usedLots = e[`${f}StockLots`];
      if (usedLots?.length) usedLots.forEach((lot, i) => addUse("entries", e, e[f], lot.kg, lot.tracecode, "", `${f}:${i}`, true));
      else addUse("entries", e, e[f], e[`${f}Kg`], e[`${f}Trace`], "", f);
    });
  });
  (data.halffabRuns || []).forEach((r) => (r.verbruik || []).forEach((v, i) => {
    if (v?.categorie === category) addUse("halffabRuns", r, v.naam, v.kg, v.eigenTracecode || v.batchnr || v.partijnummer, v.expeditieId, i);
  }));
  (data.afkeur || []).forEach((a) => { if (a?.categorie === category && !a.teruggedraaid) addUse("afkeur", a, a.naam, a.kg, a.tracecode, ""); });
  catalog.forEach((m) => { if (m.correctie) unresolved(materialFor(m.naam)).rest += m.correctie; });
  sessions.forEach((s) => s.lines.forEach((l) => {
    const m = { ...materialFor(l.naam, l), material: l.material };
    ensureLot(m, l.tracecode, { ...l, datum: l.datum || s.ts.slice(0, 10) });
  }));
  const seen = new Set();
  const consume = (event, lot, kg) => {
    lot.rest -= kg; lot.verbruikt += kg;
    allocations.push({ ...event, tracecode: lot.tracecode, naam: lot.naam, kg });
  };
  const apply = (event, newReceipt) => {
    if (event.receipt) {
      const lot = lots.get(event.key);
      const previousKg = Math.max(lot.rest, 0);
      const incoming = receipts.find((r) => text(r.id) === event.id);
      if (incoming && previousKg + event.kg > 0) lot.prijs = (previousKg * lot.prijs + event.kg * n(incoming.prijs ?? lot.prijs)) / (previousKg + event.kg);
      lot.rest += event.kg; lot.ontvangen += event.kg;
      if (newReceipt) lot.newlyReceived = true;
    } else if (event.key) consume(event, lots.get(event.key), event.kg);
    else {
      const candidates = [...lots.values()].filter((l) => l.material === event.material && l.tracecode && l.rest > 0 && (l.counted || !l.datum || l.datum <= event.datum)).sort((a, b) => a.datum.localeCompare(b.datum) || a.key.localeCompare(b.key));
      let remaining = event.kg;
      if (category === "Hulpstof") candidates.forEach((lot) => { const kg = Math.min(remaining, lot.rest); if (kg > 0) consume(event, lot, kg); remaining -= kg; });
      if (remaining > 0) {
        const material = catalog.find((m) => m.material === event.material) || [...lots.values()].find((l) => l.material === event.material);
        consume(event, unresolved(material), remaining);
      }
    }
  };
  events.sort((a, b) => a.datum.localeCompare(b.datum) || Number(b.receipt) - Number(a.receipt) || a.eventKey.localeCompare(b.eventKey));
  sessions.forEach((s, index) => {
    const atCount = new Set(sourceKeys.flatMap((key) => (s.seen[key] || []).map((id) => `${key}:${id}`)));
    events.filter((e) => !seen.has(`${e.source}:${e.id}`) && atCount.has(`${e.source}:${e.id}`)).forEach((e) => apply(e, index > 0));
    atCount.forEach((key) => seen.add(key));
    s.lines.forEach((l) => {
      const lot = lots.get(stockKey(l.material, l.tracecode));
      lot.rest = n(l.geteld); lot.counted = true; lot.count = { geteld: l.geteld, ts: s.ts, door: s.door }; lot.prijs = n(l.prijs);
    });
  });
  events.filter((e) => !seen.has(`${e.source}:${e.id}`)).forEach((e) => apply(e, sessions.length > 0));
  const result = [...lots.values()].filter((l) => l.tracecode || l.ids.length || l.counted || clean(l.rest) !== 0).map((l) => ({ ...l, rest: clean(l.rest), waarde: clean(l.rest * l.prijs), active: l.counted || l.newlyReceived }));
  const batchRest = {};
  result.forEach((lot) => {
    let remaining = Math.max(lot.rest, 0);
    const batches = lot.ids.map((id) => receipts.find((r) => text(r.id) === id)).filter(Boolean);
    batches.forEach((batch, i) => {
      const kg = i === batches.length - 1 ? remaining : Math.min(remaining, Math.max(n(batch.kg), 0));
      batchRest[text(batch.id)] = kg; remaining -= kg;
    });
  });
  return { lots: result, batchRest, allocations, sessions, record, catalog };
}

export function stockTotals(ledger) {
  const map = new Map();
  ledger.lots.forEach((l) => {
    const row = map.get(l.material) || { naam: l.naam, artikelnummer: l.artikelnummer, type: l.type, leverancier: l.leverancier, rest: 0, waarde: 0, traces: 0 };
    row.rest += l.rest; row.waarde += l.waarde; if (l.tracecode && l.rest !== 0) row.traces += 1;
    map.set(l.material, row);
  });
  return [...map.values()].map((r) => ({ ...r, rest: clean(r.rest), prijs: r.rest ? r.waarde / r.rest : 0 }));
}
